import React, { useEffect, useRef, useState } from 'react';

export const InterviewSetupStep2: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [error, setError] = useState('');

  // 🌐 Internet
  const [internetSpeed, setInternetSpeed] = useState<number | null>(null);
  const [internetStatus, setInternetStatus] = useState<
    'good' | 'average' | 'poor'
  >('good');

  // 🎙 Mic
  const [micLevel, setMicLevel] = useState(0);
  const [micStatus, setMicStatus] = useState<
    'silent' | 'low' | 'good' | 'loud'
  >('silent');

  const [cameraResolution, setCameraResolution] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  const [cameraQuality, setCameraQuality] = React.useState<
    'excellent' | 'good' | 'poor' | 'checking'
  >('checking');

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number>();

  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  // ---------------- Internet speed ----------------
  const checkInternetSpeed = async () => {
    try {
      const startTime = performance.now();
      const response = await fetch(
        'https://speed.cloudflare.com/__down?bytes=500000',
        { cache: 'no-store' }
      );

      await response.arrayBuffer();
      const endTime = performance.now();

      const durationSeconds = (endTime - startTime) / 1000;
      const bitsLoaded = 500000 * 8;
      const speedMbps = +(bitsLoaded / durationSeconds / 1024 / 1024).toFixed(
        1
      );

      setInternetSpeed(speedMbps);

      if (speedMbps > 5) setInternetStatus('good');
      else if (speedMbps > 2) setInternetStatus('average');
      else setInternetStatus('poor');
    } catch {
      setInternetStatus('poor');
    }
  };

  // ---------------- Camera + Mic ----------------
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: true
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        // ✅ Read actual camera resolution
        const { videoWidth, videoHeight } = videoRef.current;

        if (videoWidth && videoHeight) {
          setCameraResolution({ width: videoWidth, height: videoHeight });

          if (videoWidth >= 1280 && videoHeight >= 720) {
            setCameraQuality('excellent');
          } else if (videoWidth >= 640) {
            setCameraQuality('good');
          } else {
            setCameraQuality('poor');
          }
        }

        setCameraEnabled(true);
        setError('');
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      initMic(stream);
      setCameraEnabled(true);
      setError('');
    } catch {
      setError('Camera or microphone access denied.');
    }
  };

  const initMic = (stream: MediaStream) => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const update = () => {
      analyser.getByteFrequencyData(dataArray);

      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

      const normalized = Math.min(avg / 128, 1);
      setMicLevel(normalized);

      if (normalized < 0.05) setMicStatus('silent');
      else if (normalized < 0.2) setMicStatus('low');
      else if (normalized < 0.6) setMicStatus('good');
      else setMicStatus('loud');

      animationRef.current = requestAnimationFrame(update);
    };

    update();
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      (videoRef.current.srcObject as MediaStream)
        .getTracks()
        .forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }

    audioContextRef.current?.close();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    setCameraEnabled(false);
    setCameraResolution(null);
    setCameraQuality('checking');
  };

  const toggleCamera = async () => {
    stopCamera();
    setFacingMode(prev => (prev === 'user' ? 'environment' : 'user'));
    setTimeout(startCamera, 200);
  };

  // ---------------- Effects ----------------
  useEffect(() => {
    checkInternetSpeed();
    const interval = setInterval(checkInternetSpeed, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const internetBgColor =
    internetStatus === 'poor'
      ? 'bg-red-100 text-red-900'
      : internetStatus === 'average'
        ? 'bg-yellow-100 text-yellow-900'
        : 'bg-emerald-100 text-emerald-900';

  const micBgColor =
    micStatus === 'good'
      ? 'bg-emerald-100 text-emerald-900'
      : micStatus === 'low'
        ? 'bg-yellow-100 text-yellow-900'
        : micStatus === 'loud'
          ? 'bg-red-100 text-red-900'
          : 'bg-gray-100 text-gray-700';

  // ---------------- UI ----------------
  return (
    <div>
      <div className='mb-6 grid grid-cols-1 gap-6 md:grid-cols-2'>
        {/* Video Preview */}
        <div className='rounded-xl bg-gray-50 p-6'>
          <h3 className='mb-1 font-semibold'>Video Preview</h3>
          <p className='mb-4 text-sm text-gray-500'>
            Position yourself in the frame
          </p>

          <div className='relative h-72 overflow-hidden rounded-xl bg-slate-900'>
            {!cameraEnabled && (
              <div className='absolute inset-0 flex flex-col items-center justify-center text-gray-300'>
                <button className='underline' onClick={startCamera}>
                  Allow Camera & Mic
                </button>
              </div>
            )}

            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className={`h-full w-full object-cover ${
                cameraEnabled ? 'block' : 'hidden'
              }`}
            />

            {cameraEnabled && (
              <div className='absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-4'>
                <ControlButton label='🎤' />
                <ControlButton label='🔄' onClick={toggleCamera} />
              </div>
            )}
          </div>

          {error && <p className='mt-2 text-sm text-red-600'>{error}</p>}
        </div>

        {/* System Check */}
        <div className='rounded-lg p-4'>
          <h3 className='mb-4 font-semibold'>System Check</h3>

          <div className='space-y-3 text-sm'>
            <SystemItem
              title='Camera Quality'
              status={
                cameraQuality === 'excellent'
                  ? '✓ Excellent'
                  : cameraQuality === 'good'
                    ? '⚠ Good'
                    : cameraQuality === 'poor'
                      ? '✕ Poor'
                      : 'Checking'
              }
              desc={
                cameraQuality === 'poor'
                  ? 'Low resolution camera detected.'
                  : 'Camera resolution detected.'
              }
              meta={
                cameraResolution
                  ? `${cameraResolution.width}×${cameraResolution.height}`
                  : '—'
              }
              className={
                cameraQuality === 'excellent'
                  ? 'bg-emerald-100 text-emerald-900'
                  : cameraQuality === 'good'
                    ? 'bg-yellow-100 text-yellow-900'
                    : cameraQuality === 'poor'
                      ? 'bg-red-100 text-red-900'
                      : 'bg-gray-100 text-gray-700'
              }
            />

            <SystemItem
              title='Microphone'
              status={
                micStatus === 'good'
                  ? '✓ Working'
                  : micStatus === 'low'
                    ? '⚠ Low'
                    : micStatus === 'loud'
                      ? '⚠ Too Loud'
                      : '✕ No Input'
              }
              desc={
                micStatus === 'silent'
                  ? 'No sound detected'
                  : 'Live audio input detected'
              }
              meta='Live'
              className={micBgColor}
            >
              <div className='mt-2 h-2 rounded bg-white/60'>
                <div
                  className='h-2 rounded bg-black/70 transition-all'
                  style={{ width: `${micLevel * 100}%` }}
                />
              </div>
            </SystemItem>

            <SystemItem
              title='Lighting'
              status='⚠ Good'
              desc='Lighting could be improved.'
              meta='72/100'
            />

            <SystemItem
              title='Internet'
              status={
                internetStatus === 'good'
                  ? '✓ Fast'
                  : internetStatus === 'average'
                    ? '⚠ Average'
                    : '✕ Slow'
              }
              desc='Connection quality'
              meta={internetSpeed ? `${internetSpeed} Mbps` : 'Checking'}
              className={internetBgColor}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------- Shared UI ----------------
const ControlButton = ({
  label,
  onClick
}: {
  label: string;
  onClick?: () => void;
}) => (
  <button
    onClick={onClick}
    className='flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-gray-300 hover:bg-slate-700'
  >
    {label}
  </button>
);

const SystemItem: React.FC<any> = ({
  title,
  status,
  desc,
  meta,
  className,
  children
}) => (
  <div className={`rounded-lg p-4 ${className}`}>
    <div className='flex justify-between font-medium'>
      <span>
        {title} — {status}
      </span>
      <span className='text-xs'>{meta}</span>
    </div>
    <p className='mt-1 text-xs'>{desc}</p>
    {children}
  </div>
);
